<template>
    <div>
        <div class="md:flex">
            <div
                v-if="category.display_filters || category.tags.length > 0 || category.attributes.length > 0"
                class="md:w-1/5 md:pr-4"
            >
                <div class="mb-8">
                    <div class="-mx-4 md:mx-0 grid grid-cols-2 md:hidden bg-pink-100 divide-x divide-grey font-heading text-lg">
                        <div
                            class="flex items-center justify-center gap-2 py-2"
                            @click="showFilteringDrawer = true"
                        >
                            <FilterIcon class="w-4 h-4 text-grey-darkest" />
                            <span>
                                Filter
                            </span>
                        </div>
                        <div
                            class="flex items-center justify-center gap-2 py-2"
                            @click="showSortingDrawer = true"
                        >
                            <span>
                                Sort
                            </span>
                            <ChevronDownIcon class="w-4 h-4 text-grey-darkest" />
                        </div>
                    </div>

                    <Drawer
                        :open="showFilteringDrawer"
                        title="Filter Results"
                        position="left"
                        @close="showFilteringDrawer = false"
                    >
                        <CategoryFilters
                            :category="category"
                            :filter-by="filterBy"
                            :tag-id="tagId"
                            :attribute-id="attributeId"
                            @change:filter-by="filterBy = $event"
                            @change:tag-id="tagId = $event"
                            @change:attribute-id="attributeId = $event"
                        />
                    </Drawer>

                    <Drawer
                        :open="showSortingDrawer"
                        title="Sort Results"
                        @close="showSortingDrawer = false"
                    >
                        <CategorySorting
                            :sort-by="sortBy"
                            :sort-direction="sortDirection"
                            class="pb-2"
                            @change:sort-by="sortBy = $event"
                            @change:sort-direction="sortDirection = $event"
                        />
                    </Drawer>

                    <div class="hidden md:flex flex-col gap-6">
                        <CategorySorting
                            :sort-by="sortBy"
                            :sort-direction="sortDirection"
                            class="pb-2 md:pt-0"
                            @change:sort-by="sortBy = $event"
                            @change:sort-direction="sortDirection = $event"
                        />
                        <CategoryFilters
                            :category="category"
                            :filter-by="filterBy"
                            :tag-id="tagId"
                            :attribute-id="attributeId"
                            @change:filter-by="filterBy = $event"
                            @change:tag-id="tagId = $event"
                            @change:attribute-id="attributeId = $event"
                        />
                    </div>

                </div>
            </div>

            <div class="md:w-full">
                <div
                    v-if="category.display_number_filter"
                    class="mb-8 md:flex md:space-x-5 items-center"
                >
                    <div>
                        <h2>Which Age Would You Like?</h2>
                        <p class="mb-3">
                            Enter a number and we'll only show you balloons that are in stock.
                        </p>
                    </div>
                    <TextInput
                        v-model="filterNumber"
                        placeholder="e.g. 18"
                    />
                </div>

                <div
                    v-show="isLoadingProducts"
                    class="flex -mx-4"
                >
                    <CatalogueTilePlaceholder class="w-1/2 lg:w-1/3 mx-4" />
                    <CatalogueTilePlaceholder class="w-1/2 lg:w-1/3 mx-4" />
                    <CatalogueTilePlaceholder class="hidden lg:block w-1/2 lg:w-1/3 mx-4" />
                </div>

                <div v-show="!isLoadingProducts">

                    <div
                        v-show="areProducts"
                        class="grid grid-cols-2 lg:grid-cols-3 gap-4 gap-y-8 lg:gap-8 mb-8"
                    >
                        <div
                            v-for="(product, index) in products"
                            :key="index"
                        >
                            <ProductCard
                                :product="product"
                                :position="index"
                                list="Category"
                                :category="category.name"
                                :available-for-delivery="categoryAvailableForDelivery"
                                class=""
                            />
                        </div>
                    </div>

                    <div class="flex justify-center">
                        <Button
                            v-if="isLoadMoreButtonShown"
                            :loading="isLoadMoreProductsLoading"
                            :disabled="isLoadMoreProductsLoading"
                            text="Load More Products"
                            bg="2025-dark-pink"
                            @click="loadMoreProducts"
                        />
                    </div>

                    <NoSearchResultsMessage v-show="!areProducts" />

                </div>
            </div>
        </div>

    </div>
</template>

<script>

// import CategorySorting from '~/components/CategorySorting';
import debounce from 'debounce';
import TextInput from '@/components/Input';
import CategoryFiltering from '~/components/CategoryFiltering';
import CategorySearch from '~/components/CategorySearch';
import CategorySorting from '@/components/CategorySorting';
import CategoryTagSelect from '~/components/CategoryTagSelect';
import CategoryAttributeSelect from '~/components/CategoryAttributeSelect';
import ProductCard from '~/components/ProductCard';
import CatalogueTilePlaceholder from '~/components/CatalogueTilePlaceholder';
import NoSearchResultsMessage from '~/components/NoSearchResultsMessage';
import Collapse from '~/components/Collapse';
import Button from '~/components/Button';
import CategoryFilters from '~/components/CategoryFilters';
import Drawer from '~/components/Drawer';
import ChevronDownIcon from '~/static/fonts/LineIcons/chevron-down.svg';
import FilterIcon from '~/static/fonts/LineIcons/filter.svg';
/* eslint-disable-next-line */
import tailwindConfig from '@/tailwind.config.js';

export default {

    components: {
        CategorySorting,
        CategoryFiltering,
        CategorySearch,
        CategoryTagSelect,
        CategoryAttributeSelect,
        ProductCard,
        CatalogueTilePlaceholder,
        Button,
        NoSearchResultsMessage,
        Collapse,
        TextInput,
        CategoryFilters,
        Drawer,
        ChevronDownIcon,
        FilterIcon,
    },

    props: {
        category: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            products: [],
            isLoadingProducts: false,
            pageNumber: 1,
            pageLength: 24,
            isLoadMoreButtonShown: false,
            isLoadMoreProductsLoading: false,
            sortBy: 'position',
            sortDirection: 'desc',
            filterBy: 'all',
            tagId: null,
            attributeId: null,
            searchQuery: '',
            openCollapseIndex: null,
            filterNumber: '',
            showFilteringDrawer: false,
            showSortingDrawer: false,
        };
    },

    computed: {
        areFiltersApplied() {
            return this.sortBy !== 'position'
                || this.sortDirection !== 'asc'
                || this.filterBy !== 'all'
                || this.filterNumber
                || this.searchQuery !== ''
                || this.tagId !== null
                || this.attributeId !== null;
        },
        areProducts() {
            return !!this.products.length;
        },
        isMobile() {
            return typeof window !== 'undefined' && window.innerWidth < parseInt(tailwindConfig.theme.screens.md, 10);
        },
        collapseComponent() {
            return this.isMobile ? Collapse : 'div';
        },
        categoryAvailableForDelivery() {
            if (!this.category.check_delivery_postcode) {
                return true;
            }

            return this.$store.getters['checkout/checkProductAvailable'](this.category.shipping_class_id);
        },
    },

    watch: {
        sortBy() {
            this.updateProducts();
        },
        sortDirection() {
            this.updateProducts();
        },
        filterBy() {
            this.updateProducts();
        },
        tagId() {
            this.updateProducts();
        },
        attributeId() {
            this.updateProducts();
        },
        searchQuery() {
            this.updateProducts();
        },
        filterNumber() {
            this.updateProducts();
        },
    },

    async created() {
        await this.fetchProducts();
    },

    methods: {
        updateProducts() {
            this.resetPageNumber();
            debounce(this.fetchProducts(), 500);
        },
        resetPageNumber() {
            this.pageNumber = 1;
            this.pageLength = 24;
            this.addPageNumberToQueryString();
        },
        async fetchProductsForCategory(shouldPush = false) {
            const products = await this.$axios.$get(`/categories/${this.category.id}/products`, {
                params: {
                    page_number: this.pageNumber,
                    page_length: this.pageLength,
                    sort_by: this.sortBy,
                    sort_direction: this.sortDirection,
                    filter_by: this.filterBy,
                    tag_id: this.tagId,
                    attribute_id: this.attributeId,
                    filter_number: this.filterNumber,
                },
            });
            this.products = shouldPush ? [...this.products, ...products] : products;
            this.isLoadMoreButtonShown = products.length >= this.pageLength;
        },
        async fetchProducts() {
            this.isLoadingProducts = true;
            if (this.$router.currentRoute.query.page) {
                const queryPage = parseInt(this.$router.currentRoute.query.page, 10);
                const defaultPageLength = this.pageLength;
                this.pageLength = queryPage * defaultPageLength;
                await this.fetchProductsForCategory();
                this.pageLength = defaultPageLength;
                this.pageNumber = queryPage;
            } else {
                await this.fetchProductsForCategory();
            }
            this.isLoadingProducts = false;
        },
        async loadMoreProducts() {
            this.isLoadMoreProductsLoading = true;
            this.pageNumber += 1;
            this.addPageNumberToQueryString();
            await this.fetchProductsForCategory(true);
            this.isLoadMoreProductsLoading = false;
        },
        addPageNumberToQueryString() {
            this.$router.push({
                path: this.$router.currentRoute.path,
                query: {
                    page: this.pageNumber,
                },
            });
        },
        toggleCollapse() {
            if (this.openCollapseIndex === 1) {
                this.closeCollapse();
            } else {
                this.openCollapse();
            }
        },
        openCollapse() {
            this.openCollapseIndex = 1;
        },
        closeCollapse() {
            this.openCollapseIndex = null;
        },
    },

};
</script>

<style lang="scss" scoped>
</style>
